import React, { useState, useEffect, useCallback, RefObject } from 'react';
import shared from '../data/js/shared';
import { useUI, useChat } from '../contexts';
import SubMenuWrapper from './sub-menu-wrapper';
import ReportUserPopup from './report-user-popup';
import { createNotification } from '../utils/dom';

const {
  blockUserLabel,
  unblockUserLabel,
  reportUserLabel,
} = shared.submenus;

const {
  userBlockedNotice,
  userUnblockedNotice,
  userReportedNotice,
  reportError,
  blockError,
  unblockError,
} = shared.userReport;

interface ChatUserMenuProps {
  companion: {
    id: string | number;
    name?: string;
    firstName?: string;
    lastName?: string;
  } | null;
  triggerRef?: RefObject<HTMLElement | null>;
}

const ChatUserMenu: React.FC<ChatUserMenuProps> = ({ companion, triggerRef }) => {
  const {
    chatUserMenuOpen,
    setChatUserMenuOpen,
    setPopupContents,
    setPopupOpen,
  } = useUI();
  const { blockUser, unblockUser, checkBlocked, reportUser, chatId } = useChat();
  const [isBlocked, setIsBlocked] = useState(false);
  const [loading, setLoading] = useState(false);

  // Destructure companion fields into locals so memoized callback deps match
  // the compiler-inferred (property-level) dependencies.
  const companionId = companion?.id;
  const companionFirstName = companion?.firstName;
  const companionName = companion?.name;

  // Check blocked status when menu opens
  useEffect(() => {
    const fetchBlockedStatus = async () => {
      if (chatUserMenuOpen && companionId) {
        const status = await checkBlocked(Number(companionId));
        setIsBlocked(status.isBlocked);
      }
    };
    void fetchBlockedStatus();
  }, [chatUserMenuOpen, companionId, checkBlocked]);

  const handleBlock = useCallback(async () => {
    // The menu only renders with a companion, so there is always an id here.
    if (loading) return;

    setLoading(true);
    const result = await blockUser(Number(companionId));
    setLoading(false);

    if (result.success) {
      setIsBlocked(true);
      setChatUserMenuOpen(false);
      createNotification({
        type: 'success',
        msg: userBlockedNotice,
      });
    } else {
      createNotification({
        type: 'error',
        msg: result.error || blockError,
      });
    }
  }, [companionId, loading, blockUser, setChatUserMenuOpen]);

  const handleUnblock = useCallback(async () => {
    // The menu only renders with a companion, so there is always an id here.
    if (loading) return;

    setLoading(true);
    const result = await unblockUser(Number(companionId));
    setLoading(false);

    if (result.success) {
      setIsBlocked(false);
      setChatUserMenuOpen(false);
      createNotification({
        type: 'success',
        msg: userUnblockedNotice,
      });
    } else {
      createNotification({
        type: 'error',
        msg: result.error || unblockError,
      });
    }
  }, [companionId, loading, unblockUser, setChatUserMenuOpen]);

  const handleReportClick = useCallback(() => {
    setChatUserMenuOpen(false);

    const handleReportConfirm = async (details: string) => {
      const result = await reportUser({
        userId: Number(companionId),
        details,
        chatId: chatId ? Number(chatId) : undefined,
      });

      setPopupOpen(false);
      setPopupContents(null);

      if (result.success) {
        setIsBlocked(true);
        createNotification({
          type: 'success',
          msg: userReportedNotice,
        });
      } else {
        createNotification({
          type: 'error',
          msg: result.error || reportError,
        });
      }
    };

    const handleCancel = () => {
      setPopupOpen(false);
      setPopupContents(null);
    };

    setPopupContents(
      <ReportUserPopup
        userName={companionFirstName || companionName || 'this user'}
        onConfirm={handleReportConfirm}
        onCancel={handleCancel}
      />
    );
    setPopupOpen(true);
  }, [companionId, companionFirstName, companionName, chatId, reportUser, setChatUserMenuOpen, setPopupContents, setPopupOpen]);

  if (!companion) return null;

  return (
    <SubMenuWrapper
      className="sub-menu-cont chat-user-menu-cont"
      onChange={() => {
        setChatUserMenuOpen(false);
      }}
      handle="chat-user-submenu-handle"
      expanded={chatUserMenuOpen}
      triggerRef={triggerRef}
      dynamicPosition={true}
    >
      <div className="sub-menu chat-user-menu">
        <div
          className="sub-menu-item"
          onClick={isBlocked ? handleUnblock : handleBlock}
        >
          {isBlocked ? unblockUserLabel : blockUserLabel}
        </div>
        <div className="line" />
        <div
          className="sub-menu-item"
          onClick={handleReportClick}
        >
          {reportUserLabel}
        </div>
        <img
          src="/images/submenu-pointer.png"
          className="pointer"
          alt="Menu pointer arrow"
        />
      </div>
    </SubMenuWrapper>
  );
};

export default ChatUserMenu;
